import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import {
  createGetPerson,
  getPersonCount,
  getPersons,
  getTotalPrice,
  isTupCustomer,
} from '../../../../core/states/offer/offer.selector';
import { combineLatest, distinctUntilChanged, fromEvent, Observable, of } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Router } from '@angular/router';
import { first, map } from 'rxjs/operators';
import { PersonState, SupplementaryInsuranceTypes } from '../../../../core/states/offer/offer.state';
import { removeBasicInsuranceForPersonIntent } from '../../../../core/states/offer/basic-insurance/actions';
import { supplementaryInsuranceDeselectActionMap } from './supplementary-insurance-deselector';
import { TrackingEcommerceService } from '../../../../core/services/tracking/ecommerce/tracking-ecommerce.service';
import { isNotNullOrUndefined } from '../../../../core/utils/typeUtils';
import {
  cartValueAfterRemoving,
  mapSupplementaryInsuranceItemFromState,
  priceIfPresentOrZero,
} from '../../../../core/services/tracking/ecommerce/tracking-ecommerce.utils';

export interface RemoveSupplementaryInsuranceEvent {
  personId: number;
  supplementaryInsuranceKey: SupplementaryInsuranceTypes;
}

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'ssc-basket',
  templateUrl: './basket.component.html',
  styleUrls: ['./basket.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BasketComponent implements OnInit, AfterViewChecked {
  public didJustOpen = false;

  @Input() public activePersonId$?: Observable<number | null>;

  public activePerson$: Observable<PersonState | undefined>;
  public personCount$: Observable<number>;
  public openBasketPrice$: Observable<number | undefined>;
  public closedBasketPrice$: Observable<number | undefined>;
  public loadingState$: Observable<'loading' | 'loaded'>;
  public isScrolled$: Observable<boolean>;
  public isTupCustomer$: Observable<boolean>;
  public isBasketOpen = false;
  @ViewChild('openButtonRef', { read: ElementRef })
  private openButtonRef: ElementRef;
  public scrolledPastHeader: boolean = false;
  @Input() public headerHeight: number | 72;

  constructor(
    private store: Store,
    private router: Router,
    private trackingEcommerceService: TrackingEcommerceService,
  ) {}

  @HostListener('window:scroll', [])
  public onWindowScroll(): void {
    this.scrolledPastHeader = window.scrollY > this.headerHeight;
  }

  public ngOnInit(): void {
    this.personCount$ = this.store.select(getPersonCount);
    this.isTupCustomer$ = this.store.select(isTupCustomer);

    this.isScrolled$ = fromEvent(window, 'scroll').pipe(
      map(() => window.scrollY > 0),
      distinctUntilChanged(),
    );

    this.activePerson$ = combineLatest([this.store.select(getPersons), this.activePersonId$ ?? of(null)]).pipe(
      map(([persons, activePersonId]) => {
        if (persons.length === 1) {
          return persons[0];
        }

        if (activePersonId === null) {
          // general page, don't highlight one person
          return undefined;
        }

        return persons.find((person) => person.personId === activePersonId);
      }),
    );
    this.closedBasketPrice$ = combineLatest([this.activePerson$, this.store.select(getTotalPrice)]).pipe(
      map(([activePerson, totalPrice]) => {
        if (activePerson) {
          return activePerson.totalPrice;
        }

        return totalPrice;
      }),
    );
    this.loadingState$ = this.closedBasketPrice$.pipe(map((price) => (price === undefined ? 'loading' : 'loaded')));
    this.openBasketPrice$ = this.store.select(getTotalPrice);
  }

  public ngAfterViewChecked(): void {
    if (!this.didJustOpen) {
      return;
    }
    this.openButtonRef.nativeElement.focus();
    this.didJustOpen = false;
  }

  public goToSummary(): void {
    this.isBasketOpen = false;
    this.router.navigate(['/summary']);
  }

  public goToPersons(): void {
    this.isBasketOpen = false;
    this.router.navigate(['/']);
  }

  public goToInsurance(personId: number): void {
    this.isBasketOpen = false;
    this.isTupCustomer$.pipe(untilDestroyed(this)).subscribe((tupCustomer) => {
      if (tupCustomer) {
        this.router.navigate([`/supplementary-insurance/${personId}`]);
      } else {
        this.router.navigate([`/basic-insurance/${personId}`]);
      }
    });
  }

  public removeBasicInsurance(personId: number): void {
    this.trackRemoveBasicInsurance(personId);
    this.store.dispatch(removeBasicInsuranceForPersonIntent({ personId }));
  }

  public removeSupplementaryInsurance(event: RemoveSupplementaryInsuranceEvent): void {
    this.trackRemoveSupplementaryInsurance(event);

    this.store.dispatch(
      supplementaryInsuranceDeselectActionMap[event.supplementaryInsuranceKey]({
        personId: event.personId,
      }),
    );
  }

  private trackRemoveBasicInsurance(personId: number): void {
    this.store
      .select(createGetPerson(personId))
      .pipe(first(), untilDestroyed(this))
      .subscribe((person) => {
        const basicInsurance = person.basicInsurance;
        if (isNotNullOrUndefined(basicInsurance)) {
          const basicPrice = priceIfPresentOrZero(basicInsurance.price);
          this.trackingEcommerceService.trackBasicInsuranceRemoveFromCartEvent(
            {
              type: basicInsurance.key.modelType,
              insurer: basicInsurance.key.insurer,
              price: basicPrice,
            },
            basicInsurance.franchise,
            basicInsurance.includesAccidentCoverage,
            person.details.genderOrPrenatal,
            cartValueAfterRemoving(person, basicPrice),
          );
        }
      });
  }

  private trackRemoveSupplementaryInsurance(event: RemoveSupplementaryInsuranceEvent): void {
    this.store
      .select(createGetPerson(event.personId))
      .pipe(first(), untilDestroyed(this))
      .subscribe((person) => {
        const price = person.supplementaryInsurances[event.supplementaryInsuranceKey]?.price;
        if (isNotNullOrUndefined(price)) {
          this.trackingEcommerceService.trackSupplementaryInsuranceRemoveFromCartEvent(
            mapSupplementaryInsuranceItemFromState(
              event.supplementaryInsuranceKey,
              person.supplementaryInsurances,
              person.youthPackage,
              price,
              person.details.genderOrPrenatal,
            ),
            cartValueAfterRemoving(person, price),
          );
        }
      });
  }
}
